import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AgencyCreate from "../../agency-register/ui/AgencyCreateForm";
import { getLanguageSection } from '../../../shared/services/languageService';
import {
    clearResults,
    searchAgencies,
    selectAgency,
    setSearchTerm,
} from '../../../entities/agency/model/agencySlice'
import { selectAgencyState } from '../../../entities/agency/model/selectors'

export default function AgencySearchPicker() {
    const language = useMemo(() => getLanguageSection('agentregister'), [])
    const searchLanguage = language?.search || {}
    const dispatch = useDispatch()
    const { searchTerm, results, loading } = useSelector(selectAgencyState)

    const [showPanel, setShowPanel] = useState(false)
    const [activeIndex, setActiveIndex] = useState(-1)

    const wrapperRef = useRef(null)
    const debounceRef = useRef(null)
    const inputRef = useRef(null)

    useEffect(() => {
        if (!searchTerm.trim()) {
            dispatch(clearResults())
            setActiveIndex(-1)
            return
        }

        clearTimeout(debounceRef.current)

        debounceRef.current = setTimeout(() => {
            dispatch(searchAgencies(searchTerm))
            setShowPanel(true)
        }, 1000)

        return () => clearTimeout(debounceRef.current)
    }, [searchTerm, dispatch])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('[data-agency-create-modal="true"]')) {
                return
            }

            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowPanel(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleChange = (e) => {
        dispatch(setSearchTerm(e.target.value))
        setShowPanel(true)
    }

    const handleInputFocus = (e) => {
        e.target.select()
        setShowPanel(true)
    }

    const handleClearSearch = () => {
        dispatch(setSearchTerm(''))
        dispatch(clearResults())
        setShowPanel(false)
        setActiveIndex(-1)
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    const handleSelect = (agency) => {
        dispatch(selectAgency(agency))
        setShowPanel(false)
        setActiveIndex(-1)
    }

    const handleKeyDown = (e) => {
        if (!showPanel || results.length === 0) return

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0))
        }

        if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1))
        }

        if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault()
            handleSelect(results[activeIndex])
        }

        if (e.key === 'Escape') {
            setShowPanel(false)
        }
    }

    const shouldShowPanel =
        showPanel && (loading || searchTerm.trim().length > 0)

    return (
        <div ref={wrapperRef} className="relative mx-auto mt-6 w-full max-w-3xl rounded-2xl border-2 border-orange-200 bg-[#fffaf3] p-4 shadow-[0_18px_38px_rgba(245,102,11,0.12)] md:p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <label className="m-0 inline-flex items-center gap-3 text-sm font-black uppercase tracking-wide text-[#11183f]">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f6c833] text-xs font-black text-[#11183f]">1</span>
                    {searchLanguage.label || 'Paso 1. Busca tu agencia'}
                </label>
            </div>

            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleChange}
                    onFocus={handleInputFocus}
                    onClick={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={searchLanguage.placeholder || 'Escribe el nombre de la agencia...'}
                    className="mr-login-input h-[52px] w-full rounded-xl border-2 border-orange-200 bg-white px-4 pr-12 text-base font-semibold text-[#11183f] shadow-[0_10px_24px_rgba(15,23,42,0.06)]"
                />
                {searchTerm ? (
                    <button
                        type="button"
                        className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-black text-slate-500 hover:bg-slate-50"
                        onClick={handleClearSearch}
                        aria-label={searchLanguage.clearSearchAria || 'Limpiar búsqueda'}
                    >
                        ×
                    </button>
                ) : null}

                {shouldShowPanel && (
                    <div className="absolute left-0 right-0 top-[calc(100%+10px)] z-30 overflow-hidden rounded-2xl border border-orange-200 bg-white shadow-marival">
                        <div className="border-b border-orange-100 bg-orange-50 px-4 py-3 text-xs font-black uppercase tracking-wide text-[#f5660b]">{searchLanguage.panelHeader || 'Resultados de búsqueda'}</div>

                        {loading ? (
                            <div className="px-4 py-4 text-sm font-bold text-slate-600">{searchLanguage.loading || 'Buscando agencias...'}</div>
                        ) : results.length > 0 ? (
                            <div className="max-h-80 overflow-y-auto">
                                {results.map((agency, index) => (
                                    <div
                                        key={agency.id}
                                        className={`cursor-pointer border-b border-slate-100 px-4 py-4 transition ${index === activeIndex ? 'bg-slate-50' : 'bg-white hover:bg-slate-50'}`}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => handleSelect(agency)}
                                    >
                                        <div className="mb-2 flex items-center justify-between gap-3">
                                            <div className="text-base font-black text-[#11183f]">{agency.name}</div>
                                            <div className="whitespace-nowrap rounded-full bg-[#f6c833] px-3 py-1 text-xs font-black text-[#11183f]">{searchLanguage.selectBadge || 'Seleccionar'}</div>
                                        </div>

                                        <div className="space-y-1 text-sm leading-6 text-slate-600">
                                            <div><strong>{searchLanguage.addressLabel || 'Dirección:'}</strong> {agency.address}</div>
                                            <div><strong>{searchLanguage.phoneLabel || 'Teléfono:'}</strong> {agency.phone}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : searchTerm.trim() ? (
                            <div className="px-4 py-5 text-sm text-slate-600">
                                <div className="mb-2 font-black text-[#11183f]">{searchLanguage.emptyTitle || 'No encontramos agencias'}</div>
                                <div className="mb-4 leading-6">
                                    {(searchLanguage.emptyTextPrefix || 'No hay coincidencias para')} "{searchTerm}". {searchLanguage.emptyTextSuffix || 'Puedes registrar una nueva agencia.'}
                                </div>
                                <div>
                                    <AgencyCreate
                                        withButton={true}
                                        buttonText={searchLanguage.emptyActionButtonText || 'Crear nueva agencia'}
                                        title={searchLanguage.buttonTitle || 'Nueva agencia'}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="px-4 py-5 text-sm text-slate-600">
                                {searchLanguage.emptyStateText || 'Escribe para comenzar a buscar.'}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-3 text-sm font-semibold leading-6 text-slate-600">
                {searchLanguage.helperText || 'Escribe el nombre y selecciona una agencia de la lista. Si no aparece, puedes crearla aquí mismo.'}
            </div>
        </div>
    )
}
